import ebCollectLogo from './ebcollect/ebCollect Logo.svg';
import ebCollectAdmin from './ebcollect/Admin Home Page.png';
import ebCollectAdminDark from './ebcollect/Admin Home Page Dark.png';
import ebCollectApp from './ebcollect/Collector App.png';
import ebCallLogo from './ebCall/EBCallLogo.png';
import ebCallShot from "./ebCall/Capture d'écran 2026-09-29 154426.png";
import ebMarketLogo from './ebMarket/ebMarket Logo.png';
import ebMarketImg from '../Files/ebmarket.jpg';
import loanFlexImg from '../Files/loanflex.jpg';

export interface LocalizedText {
    fr: string;
    en: string;
}

export interface ProductFeature {
    icon: string;
    title: LocalizedText;
    desc: LocalizedText;
}

export interface Product {
    slug: string;
    name: string;
    logo?: string;
    tagline: LocalizedText;
    lead: LocalizedText;
    showcase: { light: string; dark?: string; alt: string };
    showcase2?: { src: string; alt: string };
    features: ProductFeature[];
    oneliner: LocalizedText;
    signature?: LocalizedText;
}

export const pick = (text: LocalizedText, lang: string) => (lang.startsWith('fr') ? text.fr : text.en);

export const PRODUCTS: Product[] = [
    {
        slug: 'ebcollecte',
        name: 'eBCollecte',
        logo: ebCollectLogo,
        tagline: {
            fr: "La plateforme intelligente de gestion de la collecte terrain",
            en: "The intelligent field collection management platform",
        },
        lead: {
            fr: "eBCollecte est une plateforme digitale conçue pour piloter de bout en bout les opérations de collecte journalière, de retrait terrain et de gestion des commissions. Elle offre une visibilité en temps réel sur les opérations, les agents, les zones et les flux, tout en permettant aux organisations de définir leurs propres règles métier.",
            en: "eBCollecte is a digital platform designed to manage daily collection operations, field withdrawals and commission management end to end. It provides real-time visibility over operations, agents, zones and flows, while allowing organisations to define their own business rules.",
        },
        showcase: { light: ebCollectAdmin, dark: ebCollectAdminDark, alt: 'eBCollecte — tableau de bord' },
        showcase2: { src: ebCollectApp, alt: 'eBCollecte — application mobile collecteur' },
        features: [
            {
                icon: 'bolt',
                title: { fr: 'Collecte en temps réel', en: 'Real-time collection' },
                desc: {
                    fr: "Digitalise l'ensemble du processus de collecte et permet de suivre les opérations en temps réel, depuis leur initiation jusqu'à leur clôture.",
                    en: "Digitises the entire collection process and tracks operations in real time, from initiation to closure.",
                },
            },
            {
                icon: 'layers',
                title: { fr: 'Gestion multizones', en: 'Multi-zone management' },
                desc: {
                    fr: "Organise et pilote les opérations sur plusieurs zones géographiques, agences, territoires ou secteurs, avec une vision consolidée ou détaillée de chaque zone.",
                    en: "Organises and manages operations across multiple geographic zones, branches, territories or sectors, with a consolidated or detailed view of each zone.",
                },
            },
            {
                icon: 'percent',
                title: { fr: 'Commissions publiées en temps réel', en: 'Real-time commission publishing' },
                desc: {
                    fr: "Les commissions applicables sont calculées et publiées automatiquement, offrant une visibilité immédiate aux agents et aux équipes de supervision.",
                    en: "Applicable commissions are calculated and published automatically, giving agents and supervision teams immediate visibility.",
                },
            },
            {
                icon: 'tune',
                title: { fr: 'Moteur de commissions ultra-paramétrable', en: 'Highly configurable commission engine' },
                desc: {
                    fr: "Définissez des règles de commission complexes selon les montants, volumes, zones, profils, types d'opérations, périodes ou objectifs, sans remettre en cause le fonctionnement global de la plateforme.",
                    en: "Define complex commission rules based on amounts, volumes, zones, profiles, operation types, periods or targets, without disrupting the platform's overall operation.",
                },
            },
            {
                icon: 'workflow',
                title: { fr: 'Retraits terrain paramétrables', en: 'Configurable field withdrawals' },
                desc: {
                    fr: "Encadre et sécurise les opérations de retrait grâce à des workflows configurables : étapes, validations, habilitations et contrôles applicables à chaque opération.",
                    en: "Frames and secures withdrawal operations with configurable workflows: steps, validations, authorisations and controls for each operation.",
                },
            },
            {
                icon: 'offline',
                title: { fr: 'Mode online & offline', en: 'Online & offline mode' },
                desc: {
                    fr: "Les équipes terrain poursuivent leurs opérations même dans les zones à connectivité limitée. Les données se synchronisent automatiquement dès le rétablissement de la connexion.",
                    en: "Field teams keep working even in low-connectivity areas. Data is automatically synchronised as soon as the connection is restored.",
                },
            },
            {
                icon: 'map',
                title: { fr: 'Cartographie temps réel — MAP Display', en: 'Real-time mapping — MAP Display' },
                desc: {
                    fr: "Visualisez les opérations et les équipes sur une carte : positions, activités et pilotage des opérations terrain en temps réel.",
                    en: "See operations and teams on a map: positions, activity tracking and real-time control of field operations.",
                },
            },
            {
                icon: 'lock',
                title: { fr: 'MFA multiniveaux', en: 'Multi-level MFA' },
                desc: {
                    fr: "Renforce la sécurité des opérations grâce à une authentification multifacteur adaptée aux niveaux de risque, profils utilisateurs et étapes de validation.",
                    en: "Strengthens operational security with multi-factor authentication adapted to risk levels, user profiles and validation steps.",
                },
            },
            {
                icon: 'dashboard',
                title: { fr: 'Dashboard By Design', en: 'Dashboard By Design' },
                desc: {
                    fr: "Des tableaux de bord entièrement personnalisables donnent à chaque profil — direction, supervision, responsable de zone, agent terrain, contrôle ou back-office — les indicateurs qui lui sont réellement utiles.",
                    en: "Fully customisable dashboards give each profile — management, supervision, zone manager, field agent, control or back-office — the indicators that truly matter to them.",
                },
            },
            {
                icon: 'ai',
                title: { fr: 'AI By Design', en: 'AI By Design' },
                desc: {
                    fr: "L'intelligence artificielle est intégrée à l'architecture de la plateforme : détection d'anomalies, analyse des tendances, aide à la décision, optimisation des opérations et génération d'insights.",
                    en: "Artificial intelligence is built into the platform's architecture: anomaly detection, trend analysis, decision support, operations optimisation and insight generation.",
                },
            },
            {
                icon: 'visibility',
                title: { fr: 'Pilotage centralisé', en: 'Centralised control' },
                desc: {
                    fr: "Une vision consolidée de l'activité de bout en bout : qui collecte, où, quand, combien, selon quelles règles et avec quels résultats.",
                    en: "An end-to-end consolidated view of activity: who collects, where, when, how much, under which rules and with which results.",
                },
            },
            {
                icon: 'evolve',
                title: { fr: 'Conçue pour évoluer avec le métier', en: 'Built to evolve with your business' },
                desc: {
                    fr: "Grâce à son haut niveau de paramétrage, la plateforme s'adapte à différents modèles de collecte, réseaux de distribution, territoires, politiques de commissionnement et processus opérationnels.",
                    en: "Thanks to its high level of configuration, the platform adapts to different collection models, distribution networks, territories, commission policies and operational processes.",
                },
            },
        ],
        oneliner: {
            fr: "Collecter, contrôler, géolocaliser, commissionner et piloter : eBCollecte transforme les opérations terrain en un processus digital, temps réel, sécurisé et entièrement maîtrisable.",
            en: "Collect, control, geolocate, commission and manage: eBCollecte turns field operations into a digital, real-time, secure and fully controllable process.",
        },
    },
    {
        slug: 'loanflex',
        name: 'LoanFlex',
        tagline: {
            fr: "La plateforme intelligente de gestion et de décision du crédit",
            en: "The intelligent credit management and decision platform",
        },
        lead: {
            fr: "LoanFlex est une solution centralisée de gestion du processus de crédit, conçue pour digitaliser et sécuriser l'ensemble de la chaîne de décision : de la réception de la demande jusqu'à la décision, la contractualisation et le suivi du portefeuille. Elle permet aux établissements financiers de standardiser leurs processus, d'accélérer l'analyse des dossiers, de renforcer la gouvernance du crédit et de disposer d'une vision consolidée de la qualité et des risques du portefeuille.",
            en: "LoanFlex is a centralised credit process management solution, designed to digitise and secure the entire decision chain: from application intake to decision, contracting and portfolio monitoring. It enables financial institutions to standardise their processes, speed up file analysis, strengthen credit governance and gain a consolidated view of portfolio quality and risks.",
        },
        showcase: { light: loanFlexImg, alt: 'LoanFlex — décision de crédit' },
        features: [
            {
                icon: 'hub',
                title: { fr: 'Processus de crédit centralisé', en: 'Centralised credit process' },
                desc: {
                    fr: "Centralise toutes les étapes du cycle de crédit au sein d'une plateforme unique : soumission, instruction, analyse, validation, décision et suivi.",
                    en: "Centralises every step of the credit cycle in a single platform: submission, review, analysis, approval, decision and monitoring.",
                },
            },
            {
                icon: 'workflow',
                title: { fr: 'Workflow entièrement paramétrable', en: 'Fully configurable workflow' },
                desc: {
                    fr: "Définissez différents circuits de traitement selon le type de crédit, le montant, le profil du client, le segment, le niveau de risque ou le canal de distribution.",
                    en: "Define different processing circuits based on credit type, amount, customer profile, segment, risk level or distribution channel.",
                },
            },
            {
                icon: 'committee',
                title: { fr: 'Comités de crédit & pouvoirs', en: 'Credit committees & powers' },
                desc: {
                    fr: "Digitalise la gestion des comités et des délégations de pouvoirs. Les règles de validation se définissent selon les montants, niveaux hiérarchiques, agences, produits ou profils de risque.",
                    en: "Digitises committee management and delegation of authority. Validation rules can be set by amount, hierarchy level, branch, product or risk profile.",
                },
            },
            {
                icon: 'matrix',
                title: { fr: 'Matrice de délégation', en: 'Delegation matrix' },
                desc: {
                    fr: "Automatise l'orientation des dossiers vers le niveau d'autorisation approprié, tout en empêchant toute validation au-delà des habilitations accordées.",
                    en: "Automatically routes files to the appropriate authorisation level while preventing approvals beyond granted powers.",
                },
            },
            {
                icon: 'finance',
                title: { fr: 'Analyse financière approfondie', en: 'In-depth financial analysis' },
                desc: {
                    fr: "Analyse structurée des informations financières : compte de résultat, bilan, compte d'exploitation, trésorerie, capacité d'autofinancement, ratios et évolution des indicateurs clés.",
                    en: "Structured analysis of financial information: income statement, balance sheet, operating account, cash flow, self-financing capacity, ratios and key indicator trends.",
                },
            },
            {
                icon: 'scale',
                title: { fr: 'Capacité de remboursement', en: 'Repayment capacity analysis' },
                desc: {
                    fr: "Évalue la capacité du demandeur à supporter son engagement à partir des revenus, charges, flux de trésorerie, endettement existant et échéances projetées.",
                    en: "Assesses the applicant's ability to sustain their commitment based on income, expenses, cash flow, existing debt and projected maturities.",
                },
            },
            {
                icon: 'analytics',
                title: { fr: 'Ratios financiers automatisés', en: 'Automated financial ratios' },
                desc: {
                    fr: "Calcule et suit automatiquement les ratios pertinents : liquidité, solvabilité, autonomie financière, rentabilité, rotation, endettement et couverture du service de la dette.",
                    en: "Automatically computes and tracks relevant ratios: liquidity, solvency, financial autonomy, profitability, turnover, leverage and debt service coverage.",
                },
            },
            {
                icon: 'checklist',
                title: { fr: 'Analyse qualitative du dossier', en: 'Qualitative file analysis' },
                desc: {
                    fr: "Intègre les éléments non financiers de l'appréciation du risque : activité, ancienneté, expérience du dirigeant, secteur, environnement économique, garanties et qualité de la relation.",
                    en: "Incorporates the non-financial elements of risk assessment: activity, track record, management experience, sector, economic environment, collateral and relationship quality.",
                },
            },
            {
                icon: 'score',
                title: { fr: 'Scoring & rating', en: 'Scoring & rating' },
                desc: {
                    fr: "Intègre des modèles de scoring et de rating afin de standardiser l'appréciation du risque et d'améliorer la cohérence des décisions.",
                    en: "Integrates scoring and rating models to standardise risk assessment and improve decision consistency.",
                },
            },
            {
                icon: 'rule',
                title: { fr: 'Moteur de règles de décision', en: 'Decision rules engine' },
                desc: {
                    fr: "Un moteur de règles configurable automatise certains contrôles et identifie les dossiers nécessitant une analyse approfondie ou une validation spécifique.",
                    en: "A configurable rules engine automates certain checks and identifies files requiring deeper analysis or specific approval.",
                },
            },
            {
                icon: 'folder',
                title: { fr: 'Gestion documentaire intelligente', en: 'Smart document management' },
                desc: {
                    fr: "Centralise les pièces justificatives du dossier de crédit et contrôle leur présence, leur validité, leur statut et leur rattachement au dossier.",
                    en: "Centralises supporting documents for the credit file and checks their presence, validity, status and attachment to the file.",
                },
            },
            {
                icon: 'compliance',
                title: { fr: 'Check-list de conformité', en: 'Compliance checklist' },
                desc: {
                    fr: "Vérifie automatiquement que les documents et informations obligatoires sont disponibles avant le passage à l'étape suivante.",
                    en: "Automatically verifies that mandatory documents and information are available before moving to the next step.",
                },
            },
            {
                icon: 'history',
                title: { fr: 'Traçabilité complète des décisions', en: 'Full decision traceability' },
                desc: {
                    fr: "Chaque action, modification, avis et décision est historisé afin de garantir une piste d'audit complète, indispensable à la gouvernance du crédit.",
                    en: "Every action, change, opinion and decision is logged, providing a complete audit trail essential to credit governance.",
                },
            },
            {
                icon: 'flag',
                title: { fr: 'Conditions & réserves', en: 'Conditions & reservations' },
                desc: {
                    fr: "Associe à chaque décision des conditions suspensives, réserves, documents complémentaires ou exigences de conformité, avec suivi de leur réalisation.",
                    en: "Attaches conditions precedent, reservations, additional documents or compliance requirements to each decision, with tracking of their fulfilment.",
                },
            },
            {
                icon: 'shield',
                title: { fr: 'Gestion des garanties', en: 'Collateral management' },
                desc: {
                    fr: "Référence, analyse et suit les garanties associées aux crédits : nature, valeur, couverture, statut, échéance et documents justificatifs.",
                    en: "References, analyses and tracks collateral attached to loans: nature, value, coverage, status, maturity and supporting documents.",
                },
            },
            {
                icon: 'person',
                title: { fr: 'Vision consolidée du client', en: 'Consolidated customer view' },
                desc: {
                    fr: "Une vue globale de la relation crédit : engagements existants, encours, incidents, garanties et nouvelles demandes pris en compte avant toute décision.",
                    en: "A global view of the credit relationship: existing commitments, outstanding balances, incidents, collateral and new applications considered before any decision.",
                },
            },
            {
                icon: 'grid',
                title: { fr: 'Multi-produits & multi-segments', en: 'Multi-product & multi-segment' },
                desc: {
                    fr: "Gère différents produits : crédit de trésorerie, d'investissement, équipement, immobilier, PME, corporate, découvert, ligne de crédit, etc.",
                    en: "Manages different products: working capital, investment, equipment, mortgage, SME, corporate credit, overdraft, credit line, and more.",
                },
            },
            {
                icon: 'entities',
                title: { fr: 'Multi-niveaux & multi-entités', en: 'Multi-level & multi-entity' },
                desc: {
                    fr: "Adapté aux organisations disposant de plusieurs agences, directions régionales, filiales ou entités, avec des règles et pouvoirs différenciés.",
                    en: "Suited to organisations with multiple branches, regional offices, subsidiaries or entities, with differentiated rules and powers.",
                },
            },
            {
                icon: 'dashboard',
                title: { fr: 'Tableaux de bord de pilotage', en: 'Credit steering dashboards' },
                desc: {
                    fr: "Suivez les volumes de demandes, délais de traitement, taux d'approbation, montants accordés, dossiers en attente, décisions par comité et qualité du portefeuille.",
                    en: "Track application volumes, processing times, approval rates, amounts granted, pending files, committee decisions and portfolio quality.",
                },
            },
            {
                icon: 'timer',
                title: { fr: 'Suivi des SLA & délais', en: 'SLA & processing time tracking' },
                desc: {
                    fr: "Mesure le temps passé à chaque étape et identifie les goulots d'étranglement dans le processus de crédit.",
                    en: "Measures time spent at each step and identifies bottlenecks in the credit process.",
                },
            },
            {
                icon: 'bell',
                title: { fr: 'Alertes & notifications', en: 'Alerts & notifications' },
                desc: {
                    fr: "Notifie les acteurs concernés : dossiers à traiter, validations attendues, pièces manquantes, échéances et dépassements de délais.",
                    en: "Notifies the relevant actors: files to process, pending approvals, missing documents, deadlines and overdue items.",
                },
            },
            {
                icon: 'roles',
                title: { fr: 'Séparation des rôles', en: 'Segregation of duties' },
                desc: {
                    fr: "Met en œuvre les principes de séparation des tâches, double contrôle et habilitation par profil, pour renforcer la maîtrise opérationnelle.",
                    en: "Implements segregation of duties, dual control and profile-based authorisation to strengthen operational control.",
                },
            },
            {
                icon: 'audit',
                title: { fr: 'Audit & conformité', en: 'Audit & compliance' },
                desc: {
                    fr: "L'ensemble du processus est traçable : les fonctions risques, conformité, audit interne et inspection disposent d'une visibilité complète sur les décisions.",
                    en: "The entire process is traceable: risk, compliance, internal audit and inspection functions have full visibility over decisions.",
                },
            },
            {
                icon: 'api',
                title: { fr: 'API & intégration au SI', en: 'API & core system integration' },
                desc: {
                    fr: "S'intègre à l'écosystème existant : Core Banking, CRM, ERP, systèmes de scoring, KYC, GED, services de données et solutions de paiement.",
                    en: "Integrates with the existing ecosystem: core banking, CRM, ERP, scoring systems, KYC, DMS, data services and payment solutions.",
                },
            },
            {
                icon: 'ai',
                title: { fr: 'AI By Design', en: 'AI By Design' },
                desc: {
                    fr: "L'IA enrichit l'analyse du crédit : extraction d'informations financières, classification documentaire, détection d'anomalies, synthèse automatique des dossiers et assistance à l'analyse — les décisions automatisées restant encadrées par les politiques de risque.",
                    en: "AI enriches credit analysis: financial data extraction, document classification, anomaly detection, automated file summaries and analysis assistance — automated decisions remain governed by the institution's risk policies.",
                },
            },
            {
                icon: 'predict',
                title: { fr: 'Analyse prédictive & signaux faibles', en: 'Predictive analysis & weak signals' },
                desc: {
                    fr: "L'exploitation des données historiques permet d'identifier des évolutions inhabituelles, signaux de dégradation ou comportements nécessitant une attention particulière.",
                    en: "Historical data analysis identifies unusual trends, deterioration signals or behaviours requiring particular attention.",
                },
            },
            {
                icon: 'evolve',
                title: { fr: 'Architecture évolutive', en: 'Evolving architecture' },
                desc: {
                    fr: "Accompagne l'évolution des politiques de crédit sans refonte : nouveaux produits, règles, niveaux de validation, critères d'analyse ou politiques de risque.",
                    en: "Supports evolving credit policies without redesign: new products, rules, approval levels, analysis criteria or risk policies.",
                },
            },
        ],
        oneliner: {
            fr: "Analyser, scorer, décider, contrôler et tracer : LoanFlex transforme le processus de crédit en un parcours digital, structuré, sécurisé et gouverné, de la demande jusqu'à la décision.",
            en: "Analyse, score, decide, control and trace: LoanFlex turns the credit process into a digital, structured, secure and governed journey, from application to decision.",
        },
    },
    {
        slug: 'ebcall',
        name: 'eBCall',
        logo: ebCallLogo,
        tagline: {
            fr: "La plateforme intelligente de gestion des parcours et opérations",
            en: "The intelligent journey and operations management platform",
        },
        lead: {
            fr: "eBCall est une plateforme digitale puissante conçue pour simplifier, centraliser et automatiser la gestion des parcours clients et des opérations de service. Elle permet aux grandes organisations de proposer une expérience fluide, personnalisée et entièrement digitalisée.",
            en: "eBCall is a powerful digital platform designed to simplify, centralise and automate the management of customer journeys and service operations. It enables large organisations to deliver a smooth, personalised and fully digitised experience.",
        },
        showcase: { light: ebCallShot, alt: 'eBCall — file d\u2019attente digitale' },
        features: [
            {
                icon: 'booking',
                title: { fr: 'Réservation d\u2019opérations en ligne', en: 'Online operation booking' },
                desc: {
                    fr: "Le client réserve une opération à distance et reçoit automatiquement un numéro de ticket, fluidifiant son passage en agence ou sur le point de service.",
                    en: "Customers book an operation remotely and automatically receive a ticket number, streamlining their visit to the branch or service point.",
                },
            },
            {
                icon: 'kyc',
                title: { fr: 'Onboarding KYC terrain', en: 'Field KYC onboarding' },
                desc: {
                    fr: "Digitalise l'identification et l'enrôlement des clients directement sur le terrain, avec un parcours KYC structuré, rapide et sécurisé.",
                    en: "Digitises customer identification and enrolment directly in the field, with a structured, fast and secure KYC journey.",
                },
            },
            {
                icon: 'calendar',
                title: { fr: 'Prise de rendez-vous intelligente', en: 'Smart appointment scheduling' },
                desc: {
                    fr: "Les clients planifient leurs rendez-vous selon les disponibilités des agences, services ou collaborateurs, tout en optimisant l'organisation des équipes.",
                    en: "Customers schedule appointments based on branch, service or staff availability, while optimising team organisation.",
                },
            },
            {
                icon: 'form',
                title: { fr: 'Formulaires personnalisables', en: 'Customisable forms' },
                desc: {
                    fr: "Créez et configurez des formulaires selon les besoins métier, avec la capacité de lier chaque formulaire à une étape précise du parcours client.",
                    en: "Create and configure forms for your business needs, linking each form to a specific step of the customer journey.",
                },
            },
            {
                icon: 'catalog',
                title: { fr: 'Catalogue de services', en: 'Service & operations catalogue' },
                desc: {
                    fr: "Centralise l'ensemble des services dans une interface unique : versement, retrait, transfert, paiement, souscription, demande de service, réservation, etc.",
                    en: "Centralises all services in a single interface: deposit, withdrawal, transfer, payment, subscription, service request, booking, and more.",
                },
            },
            {
                icon: 'cash',
                title: { fr: 'Pré-billetage en ligne', en: 'Online pre-denomination' },
                desc: {
                    fr: "Le client consulte et renseigne à l'avance les informations liées au billetage et aux besoins en espèces, pour préparer l'opération avant son arrivée sur site.",
                    en: "Customers review and fill in denomination and cash requirements in advance, preparing the operation before arriving on site.",
                },
            },
            {
                icon: 'workflow',
                title: { fr: 'Parcours entièrement configurable', en: 'Fully configurable journeys' },
                desc: {
                    fr: "Construisez des parcours adaptés à chaque activité, avec des étapes, règles, formulaires et services configurables selon les processus de l'organisation.",
                    en: "Build journeys adapted to each activity, with configurable steps, rules, forms and services aligned with your organisation's processes.",
                },
            },
            {
                icon: 'omni',
                title: { fr: 'Expérience omnicanale', en: 'Omnichannel experience' },
                desc: {
                    fr: "Le parcours commence en ligne, se poursuit sur le terrain et se finalise en agence ou en point de service, avec une continuité de l'information à chaque étape.",
                    en: "The journey starts online, continues in the field and is completed at the branch or service point, with information continuity at every step.",
                },
            },
            {
                icon: 'sectors',
                title: { fr: 'Plateforme multi-secteurs', en: 'Multi-sector platform' },
                desc: {
                    fr: "eBCall répond aux besoins des banques, compagnies aériennes, agences de voyage, assurances, opérateurs télécoms, administrations, établissements de santé et grandes entreprises de services.",
                    en: "eBCall serves banks, airlines, travel agencies, insurers, telecom operators, public administrations, healthcare facilities and large service companies.",
                },
            },
            {
                icon: 'entities',
                title: { fr: 'Pensée pour les grandes organisations', en: 'Built for large organisations' },
                desc: {
                    fr: "Gérez différents services, agences, points de présence, profils utilisateurs et processus au sein d'un même environnement.",
                    en: "Manage different services, branches, points of presence, user profiles and processes within a single environment.",
                },
            },
        ],
        oneliner: {
            fr: "Réserver, identifier, planifier, orienter, exécuter et suivre : eBCall digitalise l'ensemble du parcours client, de la demande initiale jusqu'à la réalisation de l'opération.",
            en: "Book, identify, schedule, route, execute and track: eBCall digitises the entire customer journey, from initial request to completed operation.",
        },
    },
    {
        slug: 'ebmarket',
        name: 'eBMarket',
        logo: ebMarketLogo,
        tagline: {
            fr: "La plateforme intelligente de gestion commerciale, d'affiliation et de fidélisation",
            en: "The intelligent sales, affiliation and loyalty platform",
        },
        lead: {
            fr: "eBMarket est une plateforme digitale conçue pour créer et piloter un écosystème commercial multi-entreprises, permettant à un même vendeur, agent, apporteur d'affaires ou affilié d'accéder à plusieurs opportunités commerciales au sein d'une plateforme unique. Grâce à un code commercial unique, chaque acteur est identifié, suivi et rémunéré sur les différentes offres et entreprises auxquelles il est habilité.",
            en: "eBMarket is a digital platform designed to build and manage a multi-company commercial ecosystem, allowing a single seller, agent, business introducer or affiliate to access multiple commercial opportunities within one platform. With a unique commercial code, each actor is identified, tracked and compensated across the offers and companies they are authorised to access.",
        },
        showcase: { light: ebMarketImg, alt: 'eBMarket — écosystème commercial' },
        features: [
            {
                icon: 'code',
                title: { fr: 'Un seul code commercial', en: 'One single commercial code' },
                desc: {
                    fr: "Chaque acteur dispose d'un identifiant unique, reconnu au sein des différentes entreprises et programmes — un même vendeur commercialise plusieurs produits sans multiplier les comptes ni les outils.",
                    en: "Each actor has a unique identifier recognised across companies and programmes — a single seller markets multiple products without multiplying accounts or tools.",
                },
            },
            {
                icon: 'network',
                title: { fr: 'Écosystème multi-entreprises', en: 'Multi-company ecosystem' },
                desc: {
                    fr: "eBMarket fédère plusieurs entreprises dans une même plateforme. Chacune conserve ses produits, règles commerciales, commissions et politiques, tout en bénéficiant d'un écosystème commun.",
                    en: "eBMarket federates multiple companies in one platform. Each keeps its own products, commercial rules, commissions and policies while benefiting from a shared ecosystem.",
                },
            },
            {
                icon: 'opps',
                title: { fr: 'Un portefeuille d\u2019opportunités', en: 'A portfolio of opportunities' },
                desc: {
                    fr: "Produits financiers, assurances, voyages, télécommunications, produits physiques, services digitaux, offres promotionnelles : le réseau ne vend plus un seul produit, il dispose d'un portefeuille d'opportunités.",
                    en: "Financial products, insurance, travel, telecoms, physical goods, digital services, promotional offers: the network no longer sells a single product — it holds a portfolio of opportunities.",
                },
            },
            {
                icon: 'store',
                title: { fr: 'Marketplace de produits et services', en: 'Products & services marketplace' },
                desc: {
                    fr: "Centralise les offres des entreprises onboardées dans un catalogue unique : produits, services, tarifs, conditions commerciales, commissions et campagnes en cours.",
                    en: "Centralises onboarded companies' offers in a single catalogue: products, services, pricing, commercial terms, commissions and live campaigns.",
                },
            },
            {
                icon: 'affiliate',
                title: { fr: 'Programme d\u2019affiliation multi-entreprises', en: 'Multi-company affiliation' },
                desc: {
                    fr: "Chaque entreprise crée son programme d'affiliation avec ses règles de rémunération, objectifs et campagnes, en s'appuyant sur le réseau commercial commun.",
                    en: "Each company creates its own affiliate programme with its compensation rules, targets and campaigns, building on the platform's shared sales network.",
                },
            },
            {
                icon: 'percent',
                title: { fr: 'Commissions paramétrables', en: 'Configurable commissions' },
                desc: {
                    fr: "Montant fixe, pourcentage, commission par volume ou par produit, paliers, bonus ou combinaison de règles : chaque entreprise définit ses mécanismes de rémunération.",
                    en: "Fixed amount, percentage, per-volume or per-product commission, tiers, bonuses or combined rules: each company defines its own compensation mechanisms.",
                },
            },
            {
                icon: 'calc',
                title: { fr: 'Calcul automatique des commissions', en: 'Automatic commission calculation' },
                desc: {
                    fr: "Automatise le calcul des commissions générées par les ventes et suit en temps réel les commissions acquises, validées, en attente et payées.",
                    en: "Automates the calculation of sales commissions and tracks earned, approved, pending and paid commissions in real time.",
                },
            },
            {
                icon: 'pay',
                title: { fr: 'Paiement automatisé', en: 'Automated payouts' },
                desc: {
                    fr: "Orchestre le paiement des commissions aux vendeurs, affiliés et partenaires via les moyens de paiement disponibles, avec une traçabilité complète du cycle de rémunération.",
                    en: "Orchestrates commission payouts to sellers, affiliates and partners through available payment methods, with full traceability of the compensation cycle.",
                },
            },
            {
                icon: 'trophy',
                title: { fr: 'Paliers & challenges', en: 'Tiers & challenges' },
                desc: {
                    fr: "Objectifs de ventes, bonus, paliers, récompenses, classements et campagnes de stimulation : un acteur performant est récompensé sur plusieurs programmes commerciaux.",
                    en: "Sales targets, bonuses, tiers, rewards, leaderboards and incentive campaigns: a top performer gets rewarded across multiple commercial programmes.",
                },
            },
            {
                icon: 'network',
                title: { fr: 'Réseau commercial mutualisé', en: 'Shared sales network' },
                desc: {
                    fr: "Les entreprises onboardées bénéficient d'un réseau commercial élargi, sans avoir à construire individuellement l'intégralité de leur force de vente.",
                    en: "Onboarded companies benefit from an extended sales network without individually building their entire sales force.",
                },
            },
            {
                icon: 'bolt',
                title: { fr: 'Activation instantanée', en: 'Instant opportunity activation' },
                desc: {
                    fr: "Quand une nouvelle entreprise rejoint eBMarket, son catalogue et ses opportunités sont proposés aux acteurs habilités — de nouvelles sources de revenus pour les commerciaux existants.",
                    en: "When a new company joins eBMarket, its catalogue and opportunities become available to authorised actors — new revenue streams for existing sellers.",
                },
            },
            {
                icon: 'roles',
                title: { fr: 'Habilitations commerciales', en: 'Commercial permissions' },
                desc: {
                    fr: "Chaque acteur accède uniquement aux entreprises, produits et campagnes pour lesquels il est autorisé, selon son profil, sa zone, ses performances ou les règles de l'entreprise.",
                    en: "Each actor accesses only the companies, products and campaigns they are authorised for, based on profile, zone, performance or company rules.",
                },
            },
            {
                icon: 'dashboard',
                title: { fr: 'Dashboard commercial unifié', en: 'Unified sales dashboard' },
                desc: {
                    fr: "Une vision consolidée de l'activité : ventes, commissions, objectifs, campagnes, performances et opportunités disponibles sur les différentes entreprises.",
                    en: "A consolidated view of activity: sales, commissions, targets, campaigns, performance and opportunities across the different companies.",
                },
            },
            {
                icon: 'campaign',
                title: { fr: 'Campagnes marketing centralisées', en: 'Centralised marketing campaigns' },
                desc: {
                    fr: "Les entreprises créent et diffusent leurs campagnes auprès des réseaux concernés, avec cibles, périodes, objectifs et mécanismes de récompense définis.",
                    en: "Companies create and broadcast campaigns to the relevant networks, with defined targets, periods, goals and reward mechanisms.",
                },
            },
            {
                icon: 'automation',
                title: { fr: 'Marketing automation', en: 'Marketing automation' },
                desc: {
                    fr: "Automatise les communications selon les événements commerciaux : nouvelle opportunité, inscription, première vente, objectif atteint, baisse d'activité ou réactivation.",
                    en: "Automates communications based on commercial events: new opportunity, sign-up, first sale, target reached, activity drop or reactivation.",
                },
            },
            {
                icon: 'library',
                title: { fr: 'Bibliothèque de contenus', en: 'Content library' },
                desc: {
                    fr: "Vendeurs et affiliés disposent de contenus prêts à l'emploi : bannières, vidéos, visuels, textes, catalogues, argumentaires, offres et supports de communication officiels.",
                    en: "Sellers and affiliates get ready-to-use content: banners, videos, visuals, copy, catalogues, sales pitches, offers and official communication assets.",
                },
            },
            {
                icon: 'loyalty',
                title: { fr: 'Acquisition & fidélisation', en: 'Acquisition & loyalty' },
                desc: {
                    fr: "Transforme les clients existants en clients récurrents, ambassadeurs ou apporteurs d'affaires, grâce à des programmes de fidélité, de recommandation et de récompense.",
                    en: "Turns existing customers into repeat customers, ambassadors or business introducers through loyalty, referral and reward programmes.",
                },
            },
            {
                icon: 'segment',
                title: { fr: 'Segmentation intelligente', en: 'Smart segmentation' },
                desc: {
                    fr: "Clients, vendeurs et partenaires segmentés selon leurs comportements, performances, historique d'achat, fréquence, valeur et niveau d'engagement.",
                    en: "Customers, sellers and partners segmented by behaviour, performance, purchase history, frequency, value and engagement level.",
                },
            },
            {
                icon: 'swap',
                title: { fr: 'Cross-selling & up-selling', en: 'Cross-selling & up-selling' },
                desc: {
                    fr: "La connaissance des comportements identifie les opportunités de vente croisée et de montée en gamme entre les produits et entreprises de la plateforme.",
                    en: "Behavioural insight identifies cross-sell and up-sell opportunities across the products and companies on the platform.",
                },
            },
            {
                icon: 'recommend',
                title: { fr: 'Recommandation d\u2019opportunités', en: 'Opportunity recommendations' },
                desc: {
                    fr: "Propose à chaque acteur les offres les plus pertinentes selon son profil, son historique de ventes, sa zone géographique, ses performances ou les besoins de sa clientèle.",
                    en: "Suggests the most relevant offers to each actor based on profile, sales history, geography, performance or customer needs.",
                },
            },
            {
                icon: 'game',
                title: { fr: 'Gamification du réseau', en: 'Network gamification' },
                desc: {
                    fr: "Classements, badges, niveaux, challenges et récompenses créent une dynamique permanente autour de la performance commerciale.",
                    en: "Leaderboards, badges, levels, challenges and rewards create lasting momentum around sales performance.",
                },
            },
            {
                icon: 'bi',
                title: { fr: 'Analyse & Business Intelligence', en: 'Analytics & BI' },
                desc: {
                    fr: "Une vision consolidée de l'activité commerciale : ventes, conversions, commissions, performances des réseaux, produits, entreprises, campagnes et territoires.",
                    en: "A consolidated view of commercial activity: sales, conversions, commissions, network performance, products, companies, campaigns and territories.",
                },
            },
            {
                icon: 'ai',
                title: { fr: 'AI By Design', en: 'AI By Design' },
                desc: {
                    fr: "L'IA enrichit la plateforme : recommandation de produits, détection d'opportunités, génération de contenus, segmentation intelligente, analyse des performances et personnalisation des campagnes.",
                    en: "AI enriches the platform: product recommendations, opportunity detection, content generation, smart segmentation, performance analysis and campaign personalisation.",
                },
            },
            {
                icon: 'api',
                title: { fr: 'API & intégration Enterprise', en: 'API & enterprise integration' },
                desc: {
                    fr: "S'intègre à l'écosystème existant : CRM, ERP, Core Banking, plateformes e-commerce, systèmes de paiement, outils marketing et solutions BI.",
                    en: "Integrates with existing ecosystems: CRM, ERP, core banking, e-commerce platforms, payment systems, marketing tools and BI solutions.",
                },
            },
            {
                icon: 'trace',
                title: { fr: 'Sécurité & traçabilité', en: 'Security & traceability' },
                desc: {
                    fr: "Chaque opération est rattachée à un code commercial, une entreprise, un produit, une campagne et une transaction — traçabilité complète des ventes et rémunérations.",
                    en: "Every operation is tied to a commercial code, company, product, campaign and transaction — full traceability of sales and compensation.",
                },
            },
            {
                icon: 'evolve',
                title: { fr: 'Plateforme scalable', en: 'Scalable platform' },
                desc: {
                    fr: "L'architecture permet d'intégrer progressivement de nouvelles entreprises, produits, réseaux et règles commerciales, sans remettre en cause l'écosystème existant.",
                    en: "The architecture progressively integrates new companies, products, networks and commercial rules, without disrupting the existing ecosystem.",
                },
            },
        ],
        oneliner: {
            fr: "Un seul code commercial, plusieurs entreprises, plusieurs opportunités : eBMarket transforme un réseau de vendeurs et de partenaires en un écosystème commercial intelligent, interconnecté et générateur de croissance.",
            en: "One commercial code, multiple companies, multiple opportunities: eBMarket turns a network of sellers and partners into a smart, interconnected, growth-generating commercial ecosystem.",
        },
        signature: {
            fr: "eBMarket — Un seul réseau. Plusieurs opportunités. Plus de ventes.",
            en: "eBMarket — One network. Multiple opportunities. More sales.",
        },
    },
];

export default PRODUCTS;
